import React, { useMemo } from 'react';
import {
  Box,
  BoxProps,
  MantineColor,
  MantineTheme,
  alpha,
  useMantineTheme,
  useMantineColorScheme,
} from '@mantine/core';
import {
  Chart as ChartJS,
  type ChartData,
  type ChartOptions,
  type ChartType,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

type MantineChartSeriesType = 'line' | 'bar' | 'area';

interface MantineChartSeries {
  /** Key in your data objects (e.g. "Apples", "Smartphones") */
  name: string;
  /** Label shown in legend/tooltips; defaults to `name` */
  label?: string;
  /** Mantine color like "indigo.6" or any valid CSS color (e.g. "#ff0000") */
  color?: MantineColor | string;
  /** Type of this series; "area" is a filled line */
  type?: MantineChartSeriesType;
  /** Which Y axis to bind to */
  yAxisId?: 'left' | 'right';
  /** Stack id for stacked bars/areas */
  stackId?: string;
}

type GridAxis = 'x' | 'y' | 'xy' | 'none';
type TickLine = GridAxis;

export interface MantineChartProps extends BoxProps {
  /** Source data; same shape as Mantine's charts */
  data: Record<string, any>[];

  /** X-axis key in your data objects, e.g. "date" or "month" */
  dataKey: string;

  /** Series definition (similar to @mantine/charts `series` prop) */
  series: MantineChartSeries[];

  /** Base chart type, datasets can still override with series.type */
  type?: Exclude<ChartType, 'radar' | 'polarArea'>;

  /** Show legend */
  withLegend?: boolean;

  /** Legend position (Chart.js) */
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';

  /** Which axis grid lines to show (similar to `gridAxis` in @mantine/charts) */
  gridAxis?: GridAxis;

  /** Which axis tick lines/labels to show (similar to `tickLine`) */
  tickLine?: TickLine;

  /** X-axis label text */
  xAxisLabel?: string;

  /** Left Y-axis label text */
  yAxisLabel?: string;

  /** Enable second Y axis on the right */
  withRightYAxis?: boolean;

  /** Right Y-axis label text */
  rightYAxisLabel?: string;

  /** Height in px (default 300) */
  h?: number;

  /** Format values in tooltip and axis ticks */
  valueFormatter?: (value: number) => string;

  /** Optional raw Chart.js options to merge in */
  options?: ChartOptions;
}

/**
 * Resolve Mantine theme colors like "indigo.6" or "blue" to actual hex values.
 * Falls back to theme.primaryColor when color is not provided.
 */
export function getResolvedColor(
  theme: MantineTheme,
  colorScheme: 'light' | 'dark',
  color: MantineColor | string | undefined,
  index: number
): string {
  // Figure out which primary shade we should consider "default"
  const shadeKey: 'light' | 'dark' = colorScheme === 'dark' ? 'dark' : 'light';

  const primaryShadeValue =
    typeof theme.primaryShade === 'number'
      ? theme.primaryShade
      : theme.primaryShade[shadeKey];

  const primaryShade = Number.isFinite(primaryShadeValue)
    ? primaryShadeValue
    : 6; // fallback

  if (color) {
    const [name, shadeRaw] = color.split('.');
    const palette = (theme.colors as any)[name];

    if (palette) {
      const shadeIndex = shadeRaw ? Number(shadeRaw) : primaryShade;
      const safeIndex =
        Number.isFinite(shadeIndex)
          ? Math.min(Math.max(shadeIndex, 0), palette.length - 1)
          : primaryShade;

      return palette[safeIndex];
    }

    // Not a theme color – assume it's a raw CSS color (#fff, rgb(), etc.)
    return color;
  }

  // No explicit color -> use theme.primaryColor palette
  const primaryName = theme.primaryColor;
  const palette =
    (theme.colors as any)[primaryName] ?? Object.values(theme.colors)[0];

  const idx = palette
    ? (primaryShade + index) % palette.length
    : 0;

  return palette?.[idx] ?? '#000';
}

export function MantineChart(props: MantineChartProps) {
  const {
    data,
    dataKey,
    series,
    type,
    withLegend = true,
    legendPosition = 'top',
    gridAxis = 'xy',
    tickLine = 'xy',
    xAxisLabel,
    yAxisLabel,
    withRightYAxis,
    rightYAxisLabel,
    h = 300,
    valueFormatter,
    options,
    style,
    ...boxProps
  } = props;

  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';

  const textColor = dark ? theme.colors.gray[1] : theme.colors.gray[8];
  const gridColor = alpha(dark ? theme.colors.gray[7] : theme.colors.gray[3], 0.4);

  const baseChartType: ChartType =
    type ??
    (series.some((s) => s.type === 'bar') ? 'bar' : 'line'); // works fine for mixed charts

  const chartData: ChartData = useMemo(() => {
    const labels = data.map((item) => item[dataKey] ?? '');

    const datasets = series.map((serie, index) => {
      const resolvedColor = getResolvedColor(
        theme,
        colorScheme === 'dark' ? 'dark' : 'light',
        serie.color,
        index
      );
      const isBar = serie.type === 'bar';
      const isArea = serie.type === 'area';
      const datasetType: ChartType = (serie.type === 'line' || isArea)
        ? 'line'
        : (serie.type as ChartType) || baseChartType;

      const values = data.map((item) => {
        const v = item[serie.name];
        return v ?? null;
      });

      return {
        type: datasetType,
        label: serie.label ?? serie.name,
        data: values,
        yAxisID: serie.yAxisId === 'right' ? 'yRight' : 'y',
        stack: serie.stackId,
        borderColor: resolvedColor,
        backgroundColor: isBar
          ? alpha(resolvedColor, 0.7)
          : alpha(resolvedColor, isArea ? 0.25 : 0.12),
        // nicer defaults for lines
        tension: 0.35,
        fill: isArea,
        borderWidth: isBar ? 1 : 2,
      };
    });

    return { labels, datasets };
  }, [data, dataKey, series, theme, baseChartType]);

  const chartOptions: ChartOptions = useMemo(() => {
    const gridX = gridAxis === 'x' || gridAxis === 'xy';
    const gridY = gridAxis === 'y' || gridAxis === 'xy';

    const ticksX = tickLine === 'x' || tickLine === 'xy';
    const ticksY = tickLine === 'y' || tickLine === 'xy';

    const baseOptions: ChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          display: withLegend,
          position: legendPosition,
          labels: {
            color: textColor,
            font: {
              family: theme.fontFamily,
              size: 12,
            },
            usePointStyle: true,
          },
        },
        tooltip: {
          enabled: true,
          backgroundColor: dark
            ? theme.colors.dark[7]
            : theme.colors.gray[0],
          titleColor: textColor,
          bodyColor: textColor,
          borderColor: gridColor,
          borderWidth: 1,
          callbacks: {
            label(context) {
              const label = context.dataset.label ?? '';
              const raw =
                typeof context.parsed === 'object'
                  ? // for line/bar charts parsed.y holds the value
                    (context.parsed as any).y
                  : context.parsed;

              const formatted =
                typeof raw === 'number' && valueFormatter
                  ? valueFormatter(raw)
                  : raw;

              if (!label) return String(formatted ?? '');
              return `${label}: ${formatted}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: gridX,
            color: gridColor,
          },
          ticks: {
            display: ticksX,
            color: textColor,
            font: {
              family: theme.fontFamily,
            },
          },
          title: {
            display: Boolean(xAxisLabel),
            text: xAxisLabel,
            color: textColor,
            font: {
              family: theme.fontFamily,
              weight: 'bold',
            },
          },
        },
        y: {
          grid: {
            display: gridY,
            color: gridColor,
          },
          ticks: {
            display: ticksY,
            color: textColor,
            font: {
              family: theme.fontFamily,
            },
            callback(value) {
              if (typeof value === 'number' && valueFormatter) {
                return valueFormatter(value);
              }
              return value;
            },
          },
          title: {
            display: Boolean(yAxisLabel),
            text: yAxisLabel,
            color: textColor,
            font: {
              family: theme.fontFamily,
              weight: 'bold',
            },
          },
        },
        yRight: {
          display: withRightYAxis === true,
          position: 'right',
          grid: {
            display: false,
          },
          ticks: {
            color: textColor,
            font: {
              family: theme.fontFamily,
            },
            callback(value) {
              if (typeof value === 'number' && valueFormatter) {
                return valueFormatter(value);
              }
              return value;
            },
          },
          title: {
            display: Boolean(rightYAxisLabel),
            text: rightYAxisLabel,
            color: textColor,
            font: {
              family: theme.fontFamily,
              weight: 'bold',
            },
          },
        },
      },
    };

    // Merge user options (shallow merge + nested for plugins/scales)
    if (!options) return baseOptions;

    return {
      ...baseOptions,
      ...options,
      plugins: {
        ...(baseOptions.plugins ?? {}),
        ...(options.plugins ?? {}),
      },
      scales: {
        ...(baseOptions.scales ?? {}),
        ...(options.scales ?? {}),
      },
    };
  }, [
    dark,
    gridAxis,
    tickLine,
    legendPosition,
    rightYAxisLabel,
    withLegend,
    withRightYAxis,
    xAxisLabel,
    yAxisLabel,
    textColor,
    gridColor,
    theme.fontFamily,
    options,
    valueFormatter,
  ]);

  return (
    <Box
      {...boxProps}
      style={{
        position: 'relative',
        height: h,
        width: '100%',
        ...style,
      }}
    >
      <Chart
        type={baseChartType}
        data={chartData}
        options={chartOptions}
        style={{ width: '100%', height: '100%' }}
      />
    </Box>
  );
}
